import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import type { PassageOrderFindInterface } from '@zaoblako/shared/modules/pacs/dtos/passage-order/passage-order-find.dto';
import {
  PassageOrderFormDto,
  type PassageOrderFormInterface
} from '@zaoblako/shared/modules/pacs/dtos/passage-order/passage-order-form.dto';
import { PassageOrderStatusEnum } from '@zaoblako/shared/modules/pacs/enums/passage-order-status.enum';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { PassageOrderService } from '@zaoblako/web/core/services/control/passage-order.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ExternalUserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-multi-select/external-user-multi-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { MessagesModule } from 'primeng/messages';
import { Observable } from 'rxjs';

interface EmployeeApiPayload {
  externalUser: { id: number };
}

@Component({
  selector: 'crm-passage-order-form',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    EditorComponent,
    JoinPipe,
    ExternalUserMultiSelectComponent,
    ButtonComponent,
    MessagesModule,
    ClientAutoCompleteComponent
  ],
  templateUrl: './passage-order-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PassageOrderFormComponent extends BaseFormComponent<PassageOrderFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public task = <PassageOrderFormInterface>{};
  public dto = PassageOrderFormDto;

  public selectedEmployees: ExternalUserFindInterface[] = [];

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public get PassageOrderStatusEnum() {
    return PassageOrderStatusEnum;
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly passageOrderService: PassageOrderService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.passageOrderService
      .findById<PassageOrderFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          this.task = task as any;
          this.selectedEmployees = (task.employees || []).map((empRef: any) => empRef.externalUser);
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    this.task = this.preparePayloadForApi();

    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.passageOrderService.update<PassageOrderFormInterface, PassageOrderFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Заявка №${task?.id} обновлена`);
            this.formStoreService.saveNext(task, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.passageOrderService.create<PassageOrderFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/control/passage-order/card/' + task.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  private preparePayloadForApi(): PassageOrderFormInterface {
    const transformEmployees: EmployeeApiPayload[] = [];

    if (Array.isArray(this.selectedEmployees)) {
      this.selectedEmployees.forEach((employee) => {
        if (employee && employee.id) {
          transformEmployees.push({
            externalUser: { id: employee.id }
          });
        }
      });
    }

    return {
      ...this.task,
      employees: transformEmployees as any
    };
  }

  public deleteTask(task: PassageOrderFindInterface) {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить заявку №${task.id}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.passageOrderService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(`Заявка №${task.id} удалена`);
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }
}
